import apiClient from './apiClient';

// Dashboard
export const dashboardApi = {
  get: (params?: { period?: 'week' | 'month' | 'year' | 'custom'; from?: string; to?: string }) =>
    apiClient.get('/dashboard', { params }),
};

// Excel Export
export const exportApi = {
  invoicesXlsx: () => apiClient.get('/export/invoices/xlsx', { responseType: 'blob' }),
  purchaseOrdersXlsx: () => apiClient.get('/export/purchase-orders/xlsx', { responseType: 'blob' }),
  shipmentsXlsx: () => apiClient.get('/export/shipments/xlsx', { responseType: 'blob' }),
  productsXlsx: () => apiClient.get('/export/products/xlsx', { responseType: 'blob' }),
  inventoryXlsx: () => apiClient.get('/export/inventory/xlsx', { responseType: 'blob' }),
};

// Purchase Orders
export const purchaseOrdersApi = {
  list: (params: any) => apiClient.get('/purchaseorders', { params }),
  get: (id: string) => apiClient.get(`/purchaseorders/${id}`),
  confirm: (id: string, data: any) => apiClient.post(`/purchaseorders/${id}/confirm`, data),
  sync: () => apiClient.post('/purchaseorders/sync'),
  bulkConfirm: (ids: string[]) => apiClient.post('/purchaseorders/bulk-confirm', { ids }),
  exportCsv: (params: any) => apiClient.get('/purchaseorders/export', { params, responseType: 'blob' }),
  summary: (params: any) => apiClient.get('/purchaseorders/summary', { params }),
  // Queue selected POs for SAP push -- backend flips SapSyncStatus from 0 to 1.
  // Idempotent: rows already at 1 or 2 are silently skipped.
  queueSapSync: (ids: string[]) => apiClient.post('/purchaseorders/queue-sap-sync', { ids }),
  queueSapSyncSingle: (id: string) => apiClient.post(`/purchaseorders/${id}/queue-sap-sync`),
};

// Shipments
export const shipmentsApi = {
  list: (params: any) => apiClient.get('/shipments', { params }),
  get: (id: string) => apiClient.get(`/shipments/${id}`),
  create: (data: any) => apiClient.post('/shipments', data),
  submit: (id: string) => apiClient.post(`/shipments/${id}/submit`),
  getLabel: (id: string) => apiClient.get(`/shipments/${id}/label`, { responseType: 'blob' }),
  requestLabel: (id: string) => apiClient.post(`/shipments/${id}/request-label`),
  uploadPackingDoc: (id: string, formData: FormData) =>
    apiClient.post(`/shipments/${id}/packing-documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  getPackingDoc: (docId: string) =>
    apiClient.get(`/shipments/packing-documents/${docId}`, { responseType: 'blob' }),
  fetchPackingSlip: (id: string) =>
    apiClient.post(`/shipments/${id}/fetch-packing-slip`, {}, { responseType: 'blob' }),
  bulkSubmit: (ids: string[]) => apiClient.post('/shipments/bulk-submit', { ids }),
  exportCsv: (params: any) => apiClient.get('/shipments/export', { params, responseType: 'blob' }),
};

// Invoices
export const invoicesApi = {
  list: (params: any) => apiClient.get('/invoices', { params }),
  get: (id: string) => apiClient.get(`/invoices/${id}`),
  create: (data: any) => apiClient.post('/invoices', data),
  update: (id: string, data: any) => apiClient.put(`/invoices/${id}`, data),
  delete: (id: string) => apiClient.delete(`/invoices/${id}`),
  submit: (id: string) => apiClient.post(`/invoices/${id}/submit`),
  bulkSubmit: (invoiceIds: string[]) => apiClient.post('/invoices/bulk-submit', { invoiceIds }),
  getPdf: (id: string) => apiClient.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  bulkDownload: (invoiceIds: string[]) =>
    apiClient.post('/invoices/bulk-download', { invoiceIds }, { responseType: 'blob' }),
  exportCsv: (params: any) =>
    apiClient.get('/invoices/export', { params, responseType: 'blob' }),
  importCsv: (formData: FormData) =>
    apiClient.post('/invoices/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  generateFromPo: (purchaseOrderId: string) =>
    apiClient.post(`/invoices/generate-from-po/${purchaseOrderId}`),
  createCreditNote: (invoiceId: string, data: any) =>
    apiClient.post(`/invoices/${invoiceId}/credit-note`, data),
  generatePdf: (id: string) => apiClient.post(`/invoices/${id}/generate-pdf`, {}, { responseType: 'blob' }),
  getAuditLog: (id: string) => apiClient.get(`/invoices/${id}/audit-log`),
  resubmit: (id: string) => apiClient.post(`/invoices/${id}/resubmit`),
  batchGeneratePdfs: (invoiceIds: string[]) =>
    apiClient.post('/invoices/batch-generate-pdfs', { invoiceIds }, { responseType: 'blob' }),
  syncPdfs: () => apiClient.post('/invoices/sync-pdfs'),
  importInvoice: (formData: FormData) =>
    apiClient.post('/invoices/import-invoice', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  bulkImport: (formData: FormData) =>
    apiClient.post('/invoices/bulk-import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  getCompanyProfile: () => apiClient.get('/invoices/company-profile'),
  saveCompanyProfile: (data: any) => apiClient.put('/invoices/company-profile', data),
};

// Products
export const productsApi = {
  list: (params: any) => apiClient.get('/products', { params }),
  get: (id: string) => apiClient.get(`/products/${id}`),
  update: (id: string, data: any) => apiClient.put(`/products/${id}`, data),
  syncPricing: (id: string) => apiClient.post(`/products/${id}/sync-pricing`),
  sync: () => apiClient.post('/products/sync'),
  bulkSyncPricing: (ids: string[]) => apiClient.post('/products/bulk-sync-pricing', { ids }),
  bulkUpdateStatus: (ids: string[], status: number) => apiClient.post('/products/bulk-update-status', { ids, status }),
  getAuditLog: (id: string) => apiClient.get(`/products/${id}/audit-log`),
};

// Inventory
export const inventoryApi = {
  list: (params: any) => apiClient.get('/inventory', { params }),
  update: (data: any) => apiClient.put('/inventory', data),
  syncFromAmazon: () => apiClient.post('/inventory/sync-from-amazon'),
  syncToAmazon: () => apiClient.post('/inventory/sync-to-amazon'),
};

// Users
export const usersApi = {
  list: () => apiClient.get('/users'),
  updateRole: (id: string, data: any) => apiClient.put(`/users/${id}/role`, data),
  me: () => apiClient.get('/users/me'),
};

// Files
export const filesApi = {
  upload: (formData: FormData) =>
    apiClient.post('/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  get: (id: string) => apiClient.get(`/files/${id}`, { responseType: 'blob' }),
  listForEntity: (entityType: string, entityId: string) =>
    apiClient.get(`/files/entity/${entityType}/${entityId}`),
  delete: (id: string) => apiClient.delete(`/files/${id}`),
};

// Sync
export const syncApi = {
  syncAll: () => apiClient.post('/sync/all'),
  logs: (params: any) => apiClient.get('/sync/logs', { params }),
};

// Settlement Reports
export const settlementReportsApi = {
  list: (params: any) => apiClient.get('/settlementreports', { params }),
  get: (id: string) => apiClient.get(`/settlementreports/${id}`),
  // Lazy-loaded CSV — backend keeps it out of the detail payload because settlement
  // CSVs are several MB each and shipping them on every page load is wasteful.
  getCsv: (id: string) => apiClient.get(`/settlementreports/${id}/csv`, { responseType: 'text' }),
  summary: (params: any) => apiClient.get('/settlementreports/summary', { params }),
  sync: () => apiClient.post('/settlementreports/sync'),
  updateStatus: (id: string, status: string) => apiClient.put(`/settlementreports/${id}/status`, { status }),
  // Re-parse transactions from RawCsv — repair path for reports ingested before a parser fix
  reparse: (id: string) => apiClient.post(`/settlementreports/${id}/reparse`),
  reparseAll: () => apiClient.post('/settlementreports/reparse-all'),
};

// Amazon Customer Invoices — VAT invoices issued by Amazon on Krosno's behalf to end customers (VCS)
export const amazonCustomerInvoicesApi = {
  list: (params: any) => apiClient.get('/amazoncustomerinvoices', { params }),
  get: (id: string) => apiClient.get(`/amazoncustomerinvoices/${id}`),
  summary: () => apiClient.get('/amazoncustomerinvoices/summary'),
  // Manual UI sync forces a fresh report
  sync: (params?: { fromDate?: string; toDate?: string }) =>
    apiClient.post('/amazoncustomerinvoices/sync', null, { params: { force: true, ...params } }),
  // Download PDF — first click may take 30-60s (re-requests SC_VAT_TAX_REPORT for fresh URL),
  // subsequent clicks are instant (served from DB cache)
  downloadPdf: (id: string) =>
    apiClient.get(`/amazoncustomerinvoices/${id}/pdf`, { responseType: 'blob', timeout: 180000 }),
  updateStatus: (id: string, status: string) =>
    apiClient.put(`/amazoncustomerinvoices/${id}/status`, { status }),
};

// Amazon Shipments — Fulfilled (FBA outbound to end customers) + Inbound (Krosno → FBA)
export const amazonShipmentsApi = {
  // Fulfilled
  fulfilled: {
    list: (params: any) => apiClient.get('/amazonshipments/fulfilled', { params }),
    get: (id: string) => apiClient.get(`/amazonshipments/fulfilled/${id}`),
    summary: () => apiClient.get('/amazonshipments/fulfilled/summary'),
    // Manual UI sync forces a fresh report (skips reuse) so user gets latest data
    sync: (params?: { fromDate?: string; toDate?: string; force?: boolean }) =>
      apiClient.post('/amazonshipments/fulfilled/sync', null, { params: { force: true, ...params } }),
  },
  // Inbound
  inbound: {
    list: (params: any) => apiClient.get('/amazonshipments/inbound', { params }),
    get: (id: string) => apiClient.get(`/amazonshipments/inbound/${id}`),
    summary: () => apiClient.get('/amazonshipments/inbound/summary'),
    sync: (params?: { fromDate?: string; toDate?: string }) =>
      apiClient.post('/amazonshipments/inbound/sync', null, { params }),
  },
};

// Amazon OAuth — Vendor authorization flow (consent → callback → token exchange)
export const amazonOAuthApi = {
  listVendors: () => apiClient.get('/amazon-oauth/vendors'),
  start: (accountId: string) =>
    apiClient.post('/amazon-oauth/start', { accountId }),
  exchange: (code: string, state: string, sellingPartnerId?: string) =>
    apiClient.post('/amazon-oauth/exchange', { code, state, sellingPartnerId }),
};

// Permissions
export const permissionsApi = {
  me: () => apiClient.get('/permissions/me'),
  modules: () => apiClient.get('/permissions/modules'),
  getForUser: (userId: string) => apiClient.get(`/permissions/user/${userId}`),
  setForUser: (userId: string, permissions: Record<string, string>) =>
    apiClient.put(`/permissions/user/${userId}`, permissions),
};
